import React from 'react'

import { useTranslation } from 'react-i18next'
import { useAppLocale } from '../../i18n/localeUtils'
import { formatBigMoney, formatDateMMDD, formatDateYYYYMMDD } from '../../packages/utils/numberFormatters'
import InfoBlock from '../components/InfoBlock'

import { normalizedName } from '../../components/entities/CompanyName'
import IssueTypeLabels from '../../components/entities/IssueTypeLabels'
import { IssuePublicStatus } from './IssuePublicStatus'

import { EnrichedIssueEntity } from 'types/IssueEntity'
import { CommitChart } from '../../components/entities/CommitChart'
import OutcomeChart from '../../components/entities/OutcomeChart'
import { getCommitmentsInPercentage } from './utils'
import { addBusinessDays } from 'date-fns'
import { hasSourceOfType } from '../../components/utils/calculateIssueStatus'
import { getSubPriceLabel } from '../../utils/issues/getSubPriceLabel'
import { IssueWarrantSummary } from '../../components/entities/IssueWarrantSummary'
import { IssueSingleItem } from '@finance-lab/utils/types'

type IssueSummaryComponent = React.FC<{
    hasCompanyLink?: boolean
    hasLink?: boolean
    isLoading: boolean
    isSlim?: boolean
    isIssueDetailsPage?: boolean
    issue: Pick<
        IssueSingleItem,
        | 'abbTarget'
        | 'companyCountryCode'
        | 'companyId'
        | 'currency'
        | 'fees'
        | 'hasWarrants'
        | 'id'
        | 'noInvestorsNamed'
        | 'noncashConsideration'
        | 'investors'
        | 'ipoIntervalHigh'
        | 'ipoIntervalLow'
        | 'publicationDate'
        | 'secondaryOffering'
        | 'secondarySub'
        | 'shareName'
        | 'sharesInUnit'
        | 'sources'
        | 'subEndDate'
        | 'subStartDate'
        | 'type'
        | 'totalOfferedUnits'
        | 'unitExtraContent'
        | 'unitSubPrice'
        | 'unitSubTotal'
    >
}>

const getSize = (
    issue: Pick<
        EnrichedIssueEntity,
        | 'abbTarget'
        | 'ipoIntervalHigh'
        | 'ipoIntervalLow'
        | 'secondaryOffering'
        | 'sharesInUnit'
        | 'totalOfferedUnits'
        | 'type'
        | 'unitSubPrice'
        | 'unitSubTotal'
    >,
): number | null => {
    let offeredSize = issue.offeredSize

    if (['Rights issue', 'IPO'].includes(issue.type)) {
        if (issue.type === 'IPO') {
            const offeredUnits =
                ((issue.totalOfferedUnits ?? 0) + (issue.secondaryOffering ?? 0)) * (issue.sharesInUnit ?? 1)

            const isIpoWithIntervalSubPrice =
                !issue.unitSubPrice && issue.ipoIntervalHigh !== null && issue.ipoIntervalLow !== null

            if (isIpoWithIntervalSubPrice) {
                const ipoIntervalLow = parseFloat(issue.ipoIntervalLow)
                const ipoIntervalHigh = parseFloat(issue.ipoIntervalHigh)

                offeredSize = (offeredUnits * (ipoIntervalHigh + ipoIntervalLow)) / 2
            } else {
                offeredSize = offeredUnits * (issue.unitSubPrice ?? 0)
            }
        }

        if (issue.totalOfferedUnits === null && issue.abbTarget !== null) {
            offeredSize = issue.abbTarget
        }
        return offeredSize
    }

    if (issue.unitSubTotal !== null && issue.unitSubPrice !== null) {
        offeredSize = issue.unitSubTotal * issue.unitSubPrice
    } else if (issue.abbTarget !== null) {
        offeredSize = issue.abbTarget
    }

    return offeredSize
}

export const IssueSummary: IssueSummaryComponent = ({
    hasCompanyLink = true,
    hasLink,
    issue,
    isLoading,
    isSlim,
    isIssueDetailsPage = true,
}) => {
    const { t } = useTranslation()
    const { language: locale } = useAppLocale()

    if (!issue) {
        return <InfoBlock title={t('pages.issues.summary.title')} loadingElement={false} />
    }

    const isCancelled = hasSourceOfType(issue, 'Cancellation')
    const offeredSize = getSize(issue)
    const commitments = getCommitmentsInPercentage(issue)
    const outcome =
        issue.type === 'IPO' ? issue.unitSubTotal !== null || issue.secondarySub !== null : issue.unitSubTotal !== null
    const issueSubDates =
        issue.subStartDate && issue.subEndDate
            ? `${formatDateYYYYMMDD(issue.subStartDate, { locale })} - ${formatDateYYYYMMDD(issue.subEndDate, { locale })}`
            : t('pages.issues.summary.subscriptionPeriodMissing')
    const subPrice = getSubPriceLabel({ issue, locale, showUnitToShareConversion: false })
    const sizeValue =
        offeredSize !== null
            ? formatBigMoney(offeredSize, {
                  locale,
                  currencySymbol: issue.currency,
              })
            : t('pages.issues.summary.amountMissing')
    const relevantDateLabel = ['IPO', 'Rights issue'].includes(issue.type)
        ? t('pages.issues.summary.subscriptionPeriod')
        : t('pages.issues.summary.publicationDate')
    const relevantDateValue = ['IPO', 'Rights issue'].includes(issue.type)
        ? issueSubDates
        : formatDateYYYYMMDD(issue.publicationDate, { locale })
    const commitValue =
        ['IPO', 'Rights issue'].includes(issue.type) && !isCancelled ? (
            commitments !== null ? (
                <CommitChart isLeftAlign issue={issue} skipAnimation={!isSlim} size='small' />
            ) : (
                t('pages.issues.summary.notCommunicatedYet')
            )
        ) : null
    const outcomeValue =
        ['IPO', 'Rights issue'].includes(issue.type) && !isCancelled ? (
            outcome ? (
                <OutcomeChart isLeftAlign issue={issue} skipAnimation={!isSlim} />
            ) : issue.subEndDate !== null ? (
                t('pages.issues.summary.outcomeExpectedDate', {
                    date: formatDateMMDD(addBusinessDays(new Date(issue.subEndDate), 2), { locale }),
                })
            ) : (
                t('pages.issues.summary.estimatedDateMissing')
            )
        ) : null

    return (
        <IssueWarrantSummary
            hasCompanyLink={hasCompanyLink}
            hasLink={hasLink}
            isLoading={isLoading}
            isSlim={isSlim}
            label={
                <IssueTypeLabels
                    hasWarrants={!!issue.hasWarrants}
                    isRto={!!issue.rto}
                    isSpac={!!issue.spac}
                    issueType={issue.type}
                    isClickable={false}
                    isSingleLabel={false}
                    sharesInUnit={issue.sharesInUnit}
                    unitExtraContent={issue.unitExtraContent}
                />
            }
            status={<IssuePublicStatus issue={issue} />}
            name={normalizedName(issue.shareName)}
            companyCountryCode={issue.companyCountryCode}
            typeLabel={t('pages.issues.summary.issueLabel')}
            isDetailsPage={isIssueDetailsPage}
            companyId={issue.companyId}
            sizeLabel={t('pages.issues.summary.issueSize')}
            sizeValue={sizeValue}
            subPriceValue={subPrice}
            relevantDateLabel={relevantDateLabel}
            relevantDateValue={relevantDateValue}
            commitValue={commitValue}
            outcomeValue={outcomeValue}
            detailLink={`/issues/${issue.id}`}
            noncashConsiderationValue={
                issue.type === 'Non-cash issue' && issue.noncashConsideration ? issue.noncashConsideration : null
            }
        />
    )
}
